// Kiểu dữ liệu + nhãn cho trang Đối soát của đơn vị (GĐ9, §4.6) — CHỈ ĐỌC.
// API: apps/api/src/routes/reconcile.ts (packages/finance/src/views.ts, payouts.ts, debts.ts).
// Không có số nào là giá gốc BEST: API chỉ trả dòng tiền của chính người xem.

import type { MoneyStage } from '@/components/orders/order-money'

export type UnitRole = 'agency' | 'ctv' | 'shop'

export interface ReconcileSummary {
  inTransit: number
  awaitingCarrier: number
  ready: number
  held: { debt_recovery: number; payee_locked: number }
  inVoucher: number
  paidThisMonth: number
  debt: number
  readyByKind: Record<string, number>
}

export interface ReconcileOrderRow {
  financeId: string
  shopId: string
  /** Tên đơn vị bán (chủ đơn). */
  shopName?: string | null
  orderId: string | null
  waybillCode: string | null
  /** Kết cục đơn trong sổ tiền: draft | open | delivered | returned | lost | cancelled */
  status: string
  returnStage: string | null
  deliveredAt: string | null
  cod: number
  amount: number
  byKind: Record<string, number>
  stage: MoneyStage
  holdReason: string | null
  voucherCode: string | null
  createdAt: string
}

export interface ReconcileOrdersPage {
  orders: ReconcileOrderRow[]
  nextBefore: string | null
}

export type VoucherStatus = 'draft' | 'approved' | 'paid' | 'cancelled'

export interface VoucherBuckets {
  sellerNetAmount: number
  marginAmount: number
  commissionAmount: number
  deductionAmount: number
  adjustmentAmount: number
  debtAmount: number
  paymentAmount: number
}

export interface Voucher extends VoucherBuckets {
  id: string
  code: string
  kind: 'payout' | 'offset'
  payeeKey: string
  payeeType: string
  payeeName?: string
  cutoffAt: string
  netAmount: number
  orderCount: number
  lineCount: number
  bankName: string | null
  bankAccountNumber: string | null
  bankAccountHolder: string | null
  status: VoucherStatus
  approvedAt: string | null
  paidAt: string | null
  bankRef: string | null
  cancelReason: string | null
  createdAt: string
}

export interface VoucherOrder {
  financeId: string | null
  orderId: string | null
  waybillCode: string | null
  status: string | null
  returnStage?: string | null
  cod: number | null
  lines: { kind: string; amount: number }[]
  total: number
}

export interface VoucherDetail extends Voucher {
  orders: VoucherOrder[]
}

export interface DebtLine {
  id: string
  kind: string
  amount: number
  /** payable = đang nợ · estimated = sắp phát sinh (đơn chưa có kết quả) */
  status: string
  createdAt: string
  note: string | null
  order: {
    orderId: string | null
    shopId: string
    waybillCode: string | null
    status: string
    returnStage: string | null
    codExpected: number
    deliveredAt: string | null
    returnCompletedAt: string | null
  } | null
}

export interface DebtPayment {
  id: string
  amount: number
  method: 'sepay' | 'manual' | string
  bankRef: string | null
  note: string | null
  createdAt: string
}

export interface MyDebt {
  debt: number
  lines: DebtLine[]
  payments: DebtPayment[]
  /** autoMatch: ngân hàng ShopTrack tự đối chiếu (SePay) — false thì kế toán ghi nhận tay */
  transfer: { bank: string | null; accountNumber: string | null; holder: string | null; content: string; autoMatch?: boolean }
  /** Tiền ship "Cho nợ" chưa trả, đơn chưa đối soát */
  shipDebts?: ShipDebtItem[]
}

export interface ShipDebtItem {
  orderId: string
  amount: number
  transferContent: string
  decidedAt: string | null
  waybillCode: string | null
  orderStatus: string | null
  codExpected: number | null
}

export type FollowUpStatus = 'none' | 'reminded' | 'promised' | 'paid' | 'written_off'

export interface NetworkDebtRow {
  payeeKey: string
  payeeType: string
  name: string
  phone: string | null
  parent: { id: string; name: string; tenantType: string } | null
  debt: number
  /** Tiền ship cho nợ còn mở — cộng vào nợ khi hiện */
  shipDebt?: number
  upcoming: number
  ageDays: number | null
  followUp: { status: FollowUpStatus; promisedAt: string | null; note: string | null; updatedAt: string | null }
}

export interface CommissionDeductionRow {
  month: string
  ctvShopId: string
  ctvName: string
  totalOrders: number
  monthStatus: string
  deducted: number
  paid: number
}

// ─── Nhãn ─────────────────────────────────────────────────────────

/** Loại dòng tiền → nhãn ngắn (cột "Số tiền của tôi"). */
export const KIND_LABEL: Record<string, string> = {
  seller_net: 'Thực nhận',
  margin: 'Chênh lệch',
  ctv_commission: 'Chiết khấu',
  commission_deduction: 'Trừ chiết khấu CTV',
  carrier_surcharge: 'Phí ngoài khung',
  carrier_compensation: 'Bồi thường',
  adjustment: 'Điều chỉnh',
  ship_prepaid: 'Tiền ship đã chuyển khoản trước',
  debt_payment: 'Trả nợ',
  writeoff: 'Xóa nợ',
}

export const KIND_ORDER = ['seller_net', 'carrier_surcharge', 'carrier_compensation', 'ship_prepaid', 'margin', 'ctv_commission', 'commission_deduction', 'adjustment', 'debt_payment', 'writeoff']

export function sortedKinds(byKind: Record<string, number>): [string, number][] {
  const rank = (k: string) => {
    const i = KIND_ORDER.indexOf(k)
    return i === -1 ? KIND_ORDER.length : i
  }
  return Object.entries(byKind)
    .filter(([, v]) => v !== 0)
    .sort(([a], [b]) => rank(a) - rank(b))
}

/** Kết cục đơn trong sổ tiền. */
export function outcomeLabel(status: string | null | undefined, returnStage?: string | null): string {
  if (status === 'returned') {
    if (returnStage === 'returned') return 'Hoàn — đã trả hàng về'
    if (returnStage === 'return_failed') return 'Hoàn — trả hàng không thành công'
    if (returnStage === 'returning') return 'Hoàn — đang trả hàng về'
    return 'Hoàn'
  }
  return (
    {
      draft: 'Đang tạo đơn',
      open: 'Đang giao',
      delivered: 'Giao thành công',
      lost: 'Thất lạc / hư hỏng',
      cancelled: 'Đã hủy',
    }[status ?? ''] ?? '—'
  )
}

export const STAGE_FILTERS: { value: string; label: string; shortLabel?: string }[] = [
  { value: 'all', label: 'Tất cả' },
  { value: 'in_transit', label: 'Chờ giao xong' },
  { value: 'awaiting_carrier', label: 'Chờ hãng thanh toán', shortLabel: 'Chờ hãng' },
  { value: 'ready', label: 'Sẵn sàng chi' },
  { value: 'held', label: 'Tạm giữ' },
  { value: 'in_voucher', label: 'Đã lên phiếu' },
  { value: 'paid', label: 'Đã chi' },
]

export const VOUCHER_KIND_LABEL: Record<string, string> = { payout: 'Phiếu chi', offset: 'Phiếu cấn trừ' }

export const VOUCHER_STATUS: Record<string, { text: string; cls: string }> = {
  draft: { text: 'Đang xử lý', cls: 'bg-info/12 text-info' },
  approved: { text: 'Đã duyệt — chờ chuyển khoản', cls: 'bg-accent/12 text-accent' },
  paid: { text: 'Đã chi', cls: 'bg-ok/12 text-ok' },
  cancelled: { text: 'Đã hủy', cls: 'bg-paperSoft text-inkSoft' },
}

export const FOLLOW_UP_LABEL: Record<string, string> = {
  none: 'Chưa nhắc',
  reminded: 'Đã nhắc',
  promised: 'Hẹn trả',
  paid: 'Đã trả',
  written_off: 'Đã xóa nợ',
}

export const UNIT_TYPE_LABEL: Record<string, string> = { agency: 'Đại lý', ctv: 'CTV', shop: 'Shop' }

/** Các nhóm số của phiếu (Phụ lục B) — `kinds` để đếm số đơn của từng nhóm. */
export const VOUCHER_BUCKETS: { key: keyof VoucherBuckets; label: string; hint?: string }[] = [
  { key: 'sellerNetAmount', label: 'Thực nhận đơn tự bán', hint: 'COD trừ cước theo bảng giá của bạn' },
  { key: 'marginAmount', label: 'Chênh lệch cước', hint: 'Phần hưởng trên đơn của cấp dưới' },
  { key: 'commissionAmount', label: 'Chiết khấu CTV' },
  { key: 'deductionAmount', label: 'Trừ chiết khấu trả CTV' },
  { key: 'adjustmentAmount', label: 'Điều chỉnh' },
  { key: 'debtAmount', label: 'Cấn trừ công nợ', hint: 'Đơn hoàn, đơn không thu COD, COD thấp hơn cước…' },
  { key: 'paymentAmount', label: 'Tiền bạn đã chuyển trả nợ' },
]

/** Cùng quy tắc gom nhóm với máy chủ (packages/finance/src/payouts.ts · bucketOf). */
export function bucketOf(kind: string, amount: number): keyof VoucherBuckets {
  if (kind === 'seller_net' || kind === 'carrier_surcharge' || kind === 'carrier_compensation' || kind === 'ship_prepaid') return amount < 0 ? 'debtAmount' : 'sellerNetAmount'
  if (kind === 'margin') return amount < 0 ? 'debtAmount' : 'marginAmount'
  if (kind === 'ctv_commission') return 'commissionAmount'
  if (kind === 'commission_deduction') return 'deductionAmount'
  if (kind === 'debt_payment') return 'paymentAmount'
  return 'adjustmentAmount'
}

/**
 * Vì sao một khoản âm có thể thành công nợ — nói bằng lời thường. Máy chủ chỉ trả phần âm ròng
 * của từng đơn (packages/finance/src/debts.ts · debtCandidates): khoản trừ đã nằm gọn trong tiền
 * của chính đơn đó (vd. hoa hồng CTV trừ vào chênh lệch đơn giao thành công) không xuất hiện ở đây.
 * `nppName` = tên NPP của đơn vị (useNppName) — bên điều chỉnh tiền.
 */
export function debtReason(l: DebtLine, nppName: string): string {
  const o = l.order
  if (l.kind === 'carrier_surcharge') return 'Phí ngoài khung'
  if (l.kind === 'commission_deduction') {
    const m = /^topup:(\d{4}-\d{2})$/.exec(l.note ?? '')
    return m
      ? `Bù chiết khấu CTV khi chốt tháng ${m[1]!.slice(5)}/${m[1]!.slice(0, 4)} (chế độ cũ) — tiền đơn này đã chi trước đó nên trừ vào các đơn sau`
      : 'Chiết khấu trả CTV lớn hơn chênh lệch còn lại của đơn'
  }
  if (l.kind === 'margin') return 'Chênh lệch âm — bảng giá bán cho cấp dưới đang thấp hơn giá nhập'
  if (l.kind === 'adjustment') {
    // Ghi chú máy: "from:<loại dòng>" = điều chỉnh khi số thực (cân, COD, phí BEST) khác số đã chốt
    const from = /^from:([a-z_]+)$/.exec(l.note ?? '')?.[1]
    if (from) return `Điều chỉnh ${(KIND_LABEL[from] ?? 'tiền đơn').toLowerCase()} — cân, COD hoặc phí vận chuyển thực tế khác số đã tính`
    return l.note && !/^[a-z_]+:/.test(l.note) ? `Điều chỉnh: ${l.note}` : `Điều chỉnh của ${nppName}`
  }
  if (l.kind === 'writeoff') return l.note ? `Xóa nợ: ${l.note}` : 'Xóa nợ'
  if (!o) return l.note ?? 'Khoản trừ khác'
  if (o.status === 'returned') return 'Đơn hoàn — trừ cước (và phí hoàn nếu có) theo bảng giá của bạn'
  if (o.status === 'cancelled') return 'Phí hãng tính cho đơn đã hủy'
  if (o.status === 'lost') return 'Đơn thất lạc / hư hỏng — chờ hãng bồi thường'
  if (o.codExpected === 0) return 'Đơn không thu COD — cước được tính vào công nợ'
  return 'COD thấp hơn cước theo bảng giá của bạn'
}
